/**
 * LoginPage — magic link authentication via Supabase Auth.
 * Portal: Admin Workspace
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { mcpConsentPath } from '../lib/mcpLoginContinuation';

export function LoginPage() {
  const location = useLocation();
  const auth = useAuth();
  const continuation = mcpConsentPath(location.search);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const { error: authError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: continuation ? new URL(continuation, window.location.origin).href : window.location.origin,
      },
    });

    setLoading(false);
    if (authError) {
      setError(authError.message);
    } else {
      setSent(true);
    }
  };

  if (!auth.loading && auth.session) return <Navigate to={continuation ?? '/dashboard'} replace />;

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      style={{ backgroundColor: 'var(--bg)' }}
    >
      <div className="w-full max-w-sm">
        {/* Card */}
        <div
          className="rounded-lg overflow-hidden"
          style={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}
        >
          {/* Header — fixed navy identity color */}
          <div className="px-6 py-5" style={{ backgroundColor: '#1F1E26' }}>
            <img
              src="/logo.svg"
              alt="PazNetworks, Inc."
              style={{ height: '44px', width: 'auto' }}
            />
            <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '6px' }}>
              Admin Workspace
            </p>
          </div>

          {/* Body */}
          <div className="px-6 py-5">
            {sent ? (
              <div className="text-center py-4">
                <div
                  className="w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'var(--primary-soft)' }}
                >
                  <svg className="w-6 h-6" style={{ color: 'var(--primary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                  Check your email
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  We sent a magic link to{' '}
                  <span className="font-medium" style={{ color: 'var(--text)' }}>{email}</span>
                </p>
                <button
                  onClick={() => { setSent(false); setEmail(''); }}
                  className="mt-4 text-xs underline"
                  style={{ color: 'var(--text-faint)' }}
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label
                    htmlFor="email"
                    className="block text-xs font-medium mb-1"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full h-9 px-3 text-sm rounded-sm outline-none transition-colors"
                    style={{
                      backgroundColor: 'var(--surface)',
                      border: '1px solid var(--border)',
                      color: 'var(--text)',
                    }}
                  />
                </div>

                {error && (
                  <div
                    className="px-3 py-2 rounded-sm"
                    style={{
                      backgroundColor: 'var(--error-soft)',
                      border: '1px solid var(--error)',
                    }}
                  >
                    <p className="text-xs" style={{ color: 'var(--error)' }}>{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full h-9 rounded-sm text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-primary text-text-inverse hover:bg-primary-hover"
                >
                  {loading ? 'Sending...' : 'Send magic link'}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-[10px] mt-4" style={{ color: 'var(--text-faint)' }}>
          &copy; {new Date().getFullYear()} PazNetworks, Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
